import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAppContext } from "../utils/context/app-context";
import {
	ContentContainer,
	TitleHeader,
	TableLoading,
	MaterialIcon,
} from "../components/UiComponents";
import {
	CourseHeader,
	CourseWrapper,
} from "../components/CourseComponents";
import useDocumentTitle from "../components/useDocumentTitle";

const EvaluationFormsPage = () => {
	const { dispatch, api, state } = useAppContext();
	const isOffline = state.offline;
	useDocumentTitle("Evaluation Forms");
	const [loading, setLoading] = useState(true);
	let course = api.getCurrentCourse();

	useEffect(() => {
		fetchData();
	}, []);

	async function fetchData() {
		if (isOffline) {
			setLoading(false);
			return;
		}

		const { data } = await api.fetchData(
			`/courses/${course.id}/students`
		);

		if (data?.students) {
			dispatch({
				type: "SET_COURSE_STUDENTS",
				payload: {
					students: data.students,
					course_id: course.id,
				},
			});
			setLoading(false);
		} else {
			setLoading(false);
		}
	}

	return (
		<div className="evaluation-forms-page">
			<CourseHeader />
			<ContentContainer classes="mb-4">
				<CourseWrapper>
					<TitleHeader title={`Evaluation Forms`} />
					<div className="evaluation-forms">
						{loading ? (
							<TableLoading rows={5} />
						) : (
							<>
								{course.students.length == 0 ? (
									<div className="evaluation-forms__empty">
										<p>No students have registered yet.</p>
									</div>
								) : (
									<>
										<FormsSummary students={course.students} />
										<StudentsTable students={course.students} />
									</>
								)}
							</>
						)}
					</div>
				</CourseWrapper>
			</ContentContainer>
		</div>
	);
};

const FormsSummary = ({ students }) => {
	const forms = students.map(s => s.evaluation_form).filter(f => f && f.answers);
	if (forms.length === 0) return null;

	const usefulnessCounts = {
		not_useful: 0,
		useful: 0,
		very_useful: 0
	};

	const averageScores = {
		opinion_content_structure: { sum: 0, count: 0, label: "Content & Structure" },
		opinion_duration_course: { sum: 0, count: 0, label: "Duration" },
		opinion_training_methods: { sum: 0, count: 0, label: "Training Methods" },
		opinion_breakaway_techniques: { sum: 0, count: 0, label: "Breakaway" },
		opinion_pmva_techniques: { sum: 0, count: 0, label: "PMVA" },
		opinion_search_awareness: { sum: 0, count: 0, label: "Search Awareness" },
		opinion_tutors: { sum: 0, count: 0, label: "Tutors" },
		opinion_equal_opportunities: { sum: 0, count: 0, label: "Equal Opps" },
		opinion_venue: { sum: 0, count: 0, label: "Venue" },
	};

	forms.forEach(form => {
		const ans = form.answers || {};
		if (ans.usefulness && usefulnessCounts[ans.usefulness] !== undefined) {
			usefulnessCounts[ans.usefulness]++;
		}

		Object.keys(averageScores).forEach(key => {
			if (ans[key] && !isNaN(parseInt(ans[key]))) {
				averageScores[key].sum += parseInt(ans[key]);
				averageScores[key].count++;
			}
		});
	});

	return (
		<div className="card mb-4 mt-2 bg-light" style={{ fontSize: '0.9em' }}>
			<div className="card-body p-3">
				<h6 className="mb-2"><strong>Summary ({forms.length} submitted)</strong></h6>
				<div className="row">
					<div className="col-md-5 mb-3 mb-md-0">
						<div className="mb-2 text-muted" style={{ lineHeight: '1.2' }}><strong>How useful do you feel this training has been for the work that you do?</strong></div>
						<div className="d-flex justify-content-between border-bottom pb-1 mb-1">
							<span>Not Useful:</span> <strong>{usefulnessCounts.not_useful}</strong>
						</div>
						<div className="d-flex justify-content-between border-bottom pb-1 mb-1">
							<span>Useful:</span> <strong>{usefulnessCounts.useful}</strong>
						</div>
						<div className="d-flex justify-content-between">
							<span>Very Useful:</span> <strong>{usefulnessCounts.very_useful}</strong>
						</div>
					</div>
					<div className="col-md-7 border-start">
						<div className="mb-2 text-muted"><strong>Average Scores (1-5)</strong></div>
						<div className="row">
							{Object.values(averageScores).map(score => {
								if (score.count === 0) return null;
								const avg = (score.sum / score.count).toFixed(1);
								return (
									<div className="col-sm-6 mb-1 d-flex justify-content-between border-bottom pb-1 pe-3" key={score.label}>
										<span>{score.label}:</span> <strong>{avg}</strong>
									</div>
								);
							})}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

const StudentsTable = ({ students }) => {
	if (students.length == 0) {
		return null;
	}
	return (
		<table className="table table-striped responsive-table students-table">
			<thead>
				<tr>
					<th>Name</th>
					<th className="text-center">Submitted</th>
					<th></th>
				</tr>
			</thead>
			<tbody>
				{students.map((student, index) => (
					<StudentsTableRow student={student} key={student.id === 0 ? `anon-${index}` : student.id} />
				))}
			</tbody>
		</table>
	);
};

const StudentsTableRow = ({ student }) => {
	const { state, api } = useAppContext();
	const isOffline = state.offline;
	const course = api.getCurrentCourse();
	const form = student.evaluation_form;
	const submitted = form ? true : false;

	return (
		<tr>
			<td>
				{student.first_name} {student.last_name}
			</td>
			<td className="text-center" data-label="Submitted">
				<MaterialIcon
					icon={submitted ? "check" : "close"}
					className={`text-${submitted ? "success" : "danger"
						}`}
				/>
			</td>
			<td className="text-center">
				{submitted && (
					<Link
						className="btn btn-sm btn-tertiary"
						to={`/courses/${course.id}/evaluation-forms/${form.id}`}
						style={isOffline ? { pointerEvents: "none", opacity: 0.8 } : {}}
					>
						View Form
					</Link>
				)}
			</td>
		</tr>
	);
};

export default EvaluationFormsPage;
